import { Link } from "react-router-dom"
import { Layout } from "@/components/Layout"
import { useTimesheetRecords } from "@/hooks/useTimesheetRecords"
import { AlertTriangle, CalendarCheck, Clock, FileText, Loader2, ShieldCheck, Users } from "lucide-react"
import { TimeRegistration } from "@/components/timesheet/TimeRegistration"
import { WorkSummary } from "@/components/timesheet/WorkSummary"
import { TimesheetReport } from "@/components/timesheet/TimesheetReport"
import { useAuth } from "@/hooks/useAuth"
import { useCalculateEmployeeHours } from "@/hooks/useEmployeeHoursBank"
import { formatHoursBalance } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import DashboardPontoEletronico from "@/components/dashboard/DashboardPontoEletronico"

const PontoEletronico = () => {
  const { profile } = useAuth()
  const { data: registros = [], isLoading: loadingRecords } = useTimesheetRecords()
  const { data: hoursData } = useCalculateEmployeeHours(profile?.id, profile?.condominium_id)

  const firstName = profile?.full_name?.split(" ")[0]?.toUpperCase() || "USUARIO"
  const isManager = ["administracao", "sindico", "superadmin"].includes(profile?.role || "")
  const today = new Date().toISOString().split("T")[0]
  const todayRecords = registros.filter((record) => record.record_date === today)
  const balance = hoursData?.balance || 0
  const balanceText = formatHoursBalance(balance)

  if (loadingRecords) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                Ponto Digital
              </h1>
              <Badge variant="outline" className="gap-1">
                <ShieldCheck className="h-3.5 w-3.5" />
                Modulo separavel
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Ola, {firstName}. Registre, acompanhe e feche jornadas de trabalho.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {hoursData && (
              <Badge variant={balance >= 0 ? "default" : "destructive"} className="text-sm font-mono px-3 py-2">
                <Clock className="h-4 w-4 mr-2" />
                Banco: {balanceText}
              </Badge>
            )}
            {isManager && (
              <Button asChild variant="outline">
                <Link to="/folhas-ponto">
                  <CalendarCheck className="h-4 w-4 mr-2" />
                  Fechamento
                </Link>
              </Button>
            )}
          </div>
        </div>

        <Card className="border-amber-200 bg-amber-50 text-amber-950">
          <CardContent className="p-4 flex gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-semibold">Direcionamento Portaria 671</p>
              <p className="text-xs leading-relaxed">
                Esta tela e a base operacional do modulo. Para vender como solucao fiscal completa REP-P/PTRP,
                ainda faltam AFD/AEJ no leiaute oficial, NSR imutavel, comprovante PDF, hash SHA-256,
                assinatura ICP-Brasil e Atestado Tecnico/Termo de Responsabilidade.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-600" />
                Registros Hoje
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{todayRecords.length}</div>
              <p className="text-xs text-muted-foreground">Marcacoes capturadas no dia</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4 text-emerald-600" />
                Relatorios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">8</div>
              <p className="text-xs text-muted-foreground">Tipos planejados para fechamento</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-violet-600" />
                Produto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Online</div>
              <p className="text-xs text-muted-foreground">Preparado para app web, PC ou tablet</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="registro" className="space-y-4">
          <TabsList>
            <TabsTrigger value="registro">Registro</TabsTrigger>
            <TabsTrigger value="relatorios">Relatorios</TabsTrigger>
            {isManager && <TabsTrigger value="gestao">Gestao</TabsTrigger>}
          </TabsList>

          <TabsContent value="registro" className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4 md:space-y-6">
              <TimeRegistration />
            </div>
            <div className="space-y-4 md:space-y-6">
              <WorkSummary />
              <TimesheetReport />
            </div>
          </TabsContent>

          <TabsContent value="relatorios">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Central de Relatorios de Ponto</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Gere espelho de ponto, folha de ponto, horas extras, banco de horas e absenteismo por periodo.
                </p>
                <TimesheetReport />
              </CardContent>
            </Card>
          </TabsContent>

          {isManager && (
            <TabsContent value="gestao">
              <DashboardPontoEletronico />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  )
}

export default PontoEletronico
